// =============================================================================
// Stock READ side — reproduces the sheet's getStockRequests feed shape from
// Supabase (stock_requests + stock_activity).
//
// THE DERIVATION IS A PORT, NOT A GUESS. The state machine below is a line-for-
// line port of the authoritative Apps Script handler `handleGetStockRequests`
// (ManchesterForecastEngine.gs, SECTION 17 — pulled from the live project via
// Drive export 17 Jul 2026). Every rule the first-pass reverse-engineering got
// wrong is now explicit:
//   - Requests are NOT date-scoped: a request stays visible until an explicit
//     closing event (delivered/received/undone). Do NOT add a today filter —
//     see the 2026-04-22 Riley/Meadowhall incident note in the handler.
//   - CLOSED items are emitted only while resolvedDate === today ("Collected
//     Today") or while a flag is still set. That's the "delivered items are
//     windowed to today" behaviour: a window on the EMISSION, not the walk.
//   - Flags (running_low / out_of_stock) are STICKY: a plain delivered closes
//     the request but KEEPS the flag; only back_in_stock — or a PHOTO-confirmed
//     delivery — clears it. A fresh `requested` also resets the flag.
//   - Re-requesting an already-open item is a BUMP: the original requested*
//     fields are the age/provenance anchor and must not be overwritten
//     (2026-06-21 Trafford "nothing was brought" incident).
//   - `custom_inbound` events never appear in this feed (admin activity only).
//   - Every site now runs the immediate-close-on-delivered model (2026-05-06).
//
// HOW THE TWO TABLES BECOME ONE EVENT LOG. The sheet is a single append-only
// log of action rows. Supabase splits the same taps across:
//   - stock_requests: the `requested` tap is an INSERT; undo/received are
//     STATUS UPDATES on that row (not new rows). We synthesize the missing
//     timeline events from the row's status + timestamps.
//   - stock_activity: kitchen/BOH events (running_low, out_of_stock,
//     back_in_stock, ordered, delivered) land 1:1 as rows. `wastage` is not
//     part of this feed. A `delivered` row written by the customDeliveryReceived
//     tile is the twin of the sheet's `custom_inbound` (which this feed ignores)
//     — we detect it by the action name inside idem_key and skip it.
//
// ⚠️ KNOWN GAPS (write-side, not fixable in a reader — tracked in the PR):
//   1. Command-Centre "Open Reorders" marks (magic-link / admin `ordered` rows,
//      note "Marked via magic link", staff "Reorder Owner") are written to the
//      sheet ONLY. Until JJ's CC dual-writes them into stock_activity, the
//      caller must overlay them via overlayCcOrdered() (interim) or the kitchen
//      goes silently blind to live admin reorders.
//   2. Same-day repeat taps of a STILL-OPEN item collapse onto the one-open
//      partial index, so bumpCount / lastBumped* undercount vs the sheet. A
//      same-day RE-REQUEST after a delivered/received CLOSE now IS recorded (the
//      write-side fix dropped the request idem_key and closes the delivered row,
//      freeing the index — reader test 26), and a dropped reopen shadow is caught
//      by the self-heal's insert_reopen. Cross-day re-requests land fine.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { siteIdForKey, ensureAnonSession } from "./stockSync.js";

// --- the stock read-flip gate (SEPARATE from stockSync.readFlipEnabled) ------
// readFlipEnabled already contains mcr-kitchen (for the doorbell), so reusing it
// would flip the live kitchen the instant the reader is wired — NO dark-launch.
// This gate is EMPTY by default; a flip is a reviewed one-line host add.
const STOCK_READ_HOSTS = new Set([
  // FOH TILL READ-FLIP — 31 Aug 2026 (JJ window). The FOH tills now read Supabase
  // for their OWN site's running list + storeroom banner, instead of the slow Apps
  // Script getStockRequests (measured 5-12s, which left staff unable to order at
  // Trafford's lunch peak on 31 Aug). SAFE DIRECTION — the MIRROR of the 24 Jul
  // failure: the Manchester KITCHEN (the fulfiller) STAYS ON THE SHEET (mcr-kitchen
  // is NOT in this set), and dual-WRITE (DUAL_RUN_HOSTS) puts every request on the
  // Sheet, so the kitchen still sees EVERY need — a Supabase read-miss on a till can
  // never cause a missed delivery. (24 Jul was the opposite: the kitchen-on-Supabase
  // hid needs the still-Sheet FOH tills showed, so the fulfiller went blind.)
  // Pre-flip: danger gate (tools/stock_danger_gate.mjs) GREEN — 0 genuinely-open
  // needs hidden by Supabase at tc + meadowhall. Sheet fallback on any read error;
  // dual-write stays on. Revert = re-comment these two lines + bump BUILD_VERSION.
  "trafford.eatrolled.com",
  "meadowhall.eatrolled.com",
  // KITCHEN — flipped 31 Aug 2026, AFTER the FOH tills, so this is now the SAFE
  // CONSISTENT end-state: the whole live estate reads Supabase, kitchen and tills
  // read the SAME store → NO two-store divergence. (That split — kitchen-on-Supabase
  // while the FOH tills stayed on the Sheet — is exactly what forced the 24 Jul
  // revert: backfill-instant-stamped closes hid needs the tills still showed. It
  // cannot happen now: nothing live reads the Sheet.) Danger gate green pre+post;
  // catalogue re-synced to Supabase so the kitchen's item filter shows new items;
  // dual-write on; Sheet fallback on error. The closed Arndale shop is skipped in the
  // kitchen's cross-site loop (boh-kitchen.jsx). Known trade-off: CC 'ordered' marks
  // reach the kitchen via the cached overlay, so they can lag up to ~1 poll (~60s) —
  // the durable fix is the CC dual-writing 'ordered' into stock_activity (retires the
  // overlay). Revert = re-comment this line + bump BUILD_VERSION + merge (~2 min).
  "mcr-kitchen.eatrolled.com",
]);
export function stockReadEnabled() {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get("stockread");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return STOCK_READ_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// --- London-time format helpers (the sheet backend runs in Europe/London) ----
function londonParts(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-CA", { timeZone: "Europe/London" }); // YYYY-MM-DD
  const time = d.toLocaleTimeString("en-GB", { hour12: false, timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
  return { date, time };
}
const hhmm = (iso) => londonParts(iso)?.time || "";
const ukDateOf = (iso) => { const p = londonParts(iso); if (!p) return ""; const [y, m, d] = p.date.split("-"); return `${d}/${m}/${y}`; };
const ymdToUK = (ymd) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || "")); return m ? `${m[3]}/${m[2]}/${m[1]}` : ""; };
export const londonTodayUK = () => new Date().toLocaleDateString("en-GB", { timeZone: "Europe/London" });

// The action name a kiosk write baked into its idem_key ("tc|customDeliveryReceived|Door Key|2026-07-17").
const idemAction = (key) => String(key || "").split("|")[1] || "";

// =============================================================================
// STEP 1 — synthesize the sheet's single event log from the two tables.
// Returns [{ts, tsMs, action, item, staffId, type, dateStr, note, supplierRef,
//           eta, photoPath, srcId}], chronologically sorted (the handler sorts
// by Timestamp before walking — sheet row order lied to it once; see its
// 2026-04-23 Red Hot Mayo Stickers note).
// =============================================================================
export function buildTimeline(requests, activity) {
  const evs = [];
  // sortMs = the instant used to ORDER the walk (may differ from tsMs, the
  // display instant). push() defaults sortMs to tsMs unless a caller clamps it.
  const push = (e) => { const ms = Date.parse(e.ts || ""); const tsMs = isNaN(ms) ? 0 : ms; evs.push({ ...e, tsMs, sortMs: e.sortMs != null ? e.sortMs : tsMs }); };

  // Per-item-and-DAY SERVER-clock request instants (requested_at, keyed by
  // item + request_date). Within ONE request_date the stock_requests_one_open
  // partial index (site,item,request_date WHERE status='requested') guarantees a
  // request's own close precedes the NEXT SAME-DAY request (a bump while still
  // open is swallowed). We use this to bound the close above. It MUST be scoped
  // to request_date: across days two open rows for one item legitimately coexist,
  // and a late cross-day receipt can genuinely follow a newer day's request —
  // clamping against those would wrongly reopen a collected item.
  const reqInstantsByItemDay = new Map();
  const dayKey = (item, rd) => `${item}|${rd || ""}`;
  for (const r of requests || []) {
    if (!r || !r.item_name) continue;
    const ms = Date.parse(r.requested_at || "");
    if (!isNaN(ms)) { const k = dayKey(r.item_name, r.request_date); const a = reqInstantsByItemDay.get(k) || []; a.push(ms); reqInstantsByItemDay.set(k, a); }
  }
  for (const a of reqInstantsByItemDay.values()) a.sort((x, y) => x - y);
  const nextReqAfter = (item, rd, ms) => { const a = reqInstantsByItemDay.get(dayKey(item, rd)); if (a) for (const v of a) if (v > ms) return v; return null; };

  for (const r of requests || []) {
    if (!r || !r.item_name) continue;
    const reqMs = Date.parse(r.requested_at || "");
    const reqMsSafe = isNaN(reqMs) ? 0 : reqMs;
    push({
      ts: r.requested_at, action: "requested", item: r.item_name,
      staffId: r.requested_by || null, type: r.type || "standard",
      // The sheet's Date column is the app-supplied London date; request_date
      // is exactly that (DATE, London by design — migration 0002).
      dateStr: ymdToUK(r.request_date) || ukDateOf(r.requested_at),
      srcId: r.id,
    });
    // CLOCK-SKEW CLAMP (both directions): `undone`/`received` timestamps are the
    // KIOSK clock (stock.js updated_at / received_at), while requested_at is the
    // DB SERVER clock, so a mis-set tablet clock can sort a request's own close
    // wrongly. A close can neither precede its OWN request (slow clock) nor follow
    // the NEXT SAME-DAY re-request of the same item (fast clock) — within a
    // request_date two rows imply the close happened before the reopen. Unclamped,
    // the slow case leaves a cancelled item showing OPEN, and the fast case sorts
    // the close PAST a genuine same-day re-request and CLOSES it, hiding a real
    // open need on the flipped kitchen. Clamp the close's SORT instant into
    // [its own request, the next SAME-DAY same-item request). No next same-day
    // request -> no upper bound (normal case, and every legit cross-day close).
    const nextReq = nextReqAfter(r.item_name, r.request_date, reqMsSafe);
    const clampClose = (raw) => {
      let s = Math.max(raw || 0, reqMsSafe);
      if (nextReq != null && s >= nextReq) s = nextReq - 1;
      return s;
    };
    if (r.status === "undone") {
      const ts = r.updated_at || r.requested_at;
      push({ ts, action: "undone", item: r.item_name, srcId: r.id, sortMs: clampClose(Date.parse(ts) || 0) });
    } else if (r.status === "received") {
      const ts = r.received_at || r.updated_at || r.requested_at;
      push({ ts, action: "received", item: r.item_name, staffId: r.received_by || null, dateStr: ukDateOf(ts), srcId: r.id, sortMs: clampClose(Date.parse(ts) || 0) });
    }
  }

  for (const a of activity || []) {
    if (!a || !a.item_name) continue;
    if (a.event === "wastage") continue;               // not part of this feed
    if (a.event === "delivered" && idemAction(a.idem_key) === "customDeliveryReceived") continue; // sheet twin is custom_inbound — feed ignores it
    push({
      ts: a.created_at, action: a.event, item: a.item_name,
      staffId: a.staff_id || null, dateStr: ukDateOf(a.created_at),
      note: a.note || "", supplierRef: a.supplier_ref || "",
      eta: a.eta ? `${ukDateOf(a.eta)} ${hhmm(a.eta)}` : "",
      photoPath: a.photo_path || "", srcId: a.id,
    });
  }

  // Chronological walk order; deterministic tiebreak (requested before the
  // actions that follow it, then source id) for same-instant rows.
  const rank = { requested: 0 };
  evs.sort((x, y) => x.sortMs - y.sortMs
    || (rank[x.action] ?? 1) - (rank[y.action] ?? 1)
    || String(x.srcId).localeCompare(String(y.srcId)));
  return evs;
}

// =============================================================================
// STEP 2 — the ported state walker (handleGetStockRequests, line-for-line).
// Pure: no network, no clock reads (todayStr injected) — unit-testable.
//   nameFor(uuid) -> display name ('Unknown' fallback matches the sheet rows'
//                    own default), urlFor(photoPath) -> public URL.
//   activeItemNames: lowercase Set of active catalog names, or null to skip the
//                    deactivated-item filter (the handler FAILS OPEN when its
//                    catalog read errors — null reproduces that).
// =============================================================================
export function walkStock(events, { todayStr = londonTodayUK(), nameFor = () => null, urlFor = () => "", activeItemNames = null } = {}) {
  const nm = (id) => (id && nameFor(id)) || "Unknown";
  const itemState = {};
  const touchItem = (item) => (itemState[item] ||= {
    item, open: false, flag: null,
    requestedBy: "", requestedTime: "", requestedFullTs: "", requestedDateStr: "",
    lastBumpedBy: "", lastBumpedTime: "", lastBumpedFullTs: "", lastBumpedDateStr: "", bumpCount: 0,
    flaggedBy: "", flaggedFullTs: "",
    type: "standard", deliveredAt: "", deliveredBy: "", lastStatus: "", resolvedDate: "",
    ordered: false, refImageUrl: "", supplierRef: "", eta: "", adminNote: "",
    orderedBy: "", orderedTime: "", orderedFullTs: "", orderedDateStr: "",
    // 2026-09-20: true while an `ordered` row is holding a CLOSED lifecycle open
    // (Collect already tapped, or a brand-new order nobody requested). Lets
    // back_in_stock / unordered close the item again instead of leaving it
    // "open · delivered" forever. Mirrors Apps Script handleGetStockRequests
    // @358 and the SQL twin (migration 1850). Never emitted.
    reopenedByOrder: false,
    srcId: "",
  });

  for (const ev of events || []) {
    if (!ev.item) continue;
    const st = touchItem(ev.item);
    st.srcId = ev.srcId || st.srcId; // sheet: rowIndex of the LAST row touching the item

    if (ev.action === "requested") {
      // Bump semantics (2026-06-21 fix): only a NOT-already-open item gets its
      // requested* anchor set; a re-request of an open item is recorded as a
      // bump so the item's real age stays honest across screens.
      const wasOpen = st.open;
      st.open = true;
      st.flag = null;
      const rcv = hhmm(ev.ts);
      if (!wasOpen) {
        st.requestedBy = nm(ev.staffId);
        st.requestedTime = rcv;
        st.requestedFullTs = ev.ts || "";
        st.requestedDateStr = ev.dateStr || "";
        st.type = ev.type || "standard";
        st.lastBumpedBy = ""; st.lastBumpedTime = ""; st.lastBumpedFullTs = ""; st.lastBumpedDateStr = ""; st.bumpCount = 0;
      } else {
        st.lastBumpedBy = nm(ev.staffId);
        st.lastBumpedTime = rcv;
        st.lastBumpedFullTs = ev.ts || "";
        st.lastBumpedDateStr = ev.dateStr || "";
        st.bumpCount = (st.bumpCount || 0) + 1;
      }
      // Reset stale close + ordered state from a previous lifecycle (sheet:
      // 2026-04-23 Coke Zero/Coke Regular reopen bug).
      st.deliveredAt = ""; st.deliveredBy = ""; st.lastStatus = ""; st.resolvedDate = "";
      st.ordered = false; st.refImageUrl = ""; st.supplierRef = ""; st.eta = ""; st.adminNote = "";
      st.reopenedByOrder = false;   // 2026-09-20: a real request again -> Collect owed
    } else if (ev.action === "undone") {
      st.open = false;
      st.flag = null;
      st.ordered = false;
      st.refImageUrl = ""; st.supplierRef = ""; st.eta = ""; st.adminNote = "";
      st.reopenedByOrder = false;
    } else if (ev.action === "out_of_stock" || ev.action === "running_low") {
      st.flag = ev.action;       // sticky — cleared only by back_in_stock
      st.flaggedBy = nm(ev.staffId);
      st.flaggedFullTs = ev.ts || "";
    } else if (ev.action === "back_in_stock") {
      st.flag = null;            // the only event that clears a flag
      st.ordered = false;        // back-in-stock supersedes ordered too
      st.refImageUrl = ""; st.supplierRef = ""; st.eta = ""; st.adminNote = "";
      // 2026-09-20 FIX (Trafford launch test, Blue Roll; Apps Script @358, SQL twin
      // 1850): when the ONLY thing holding this lifecycle open is an `ordered` row
      // that re-opened an already-closed request (Collect tapped before the
      // reorder), or a brand-new order nobody requested, the Inbound receipt IS
      // the resolution — close it, dated by this row, so it shows as collected
      // today and then drops. Marker unset (request → ordered → receipt, no
      // Collect yet) stays open for the Collect tap, exactly as before.
      if (st.reopenedByOrder) {
        st.open = false;
        st.resolvedDate = ev.dateStr || "";
        st.reopenedByOrder = false;
      }
    } else if (ev.action === "unordered") {
      // Written by the Command Centre's own Undo (0741) and, since migration
      // 1540, by the ordered-state parity job's service-role door
      // (cc_stock_record_unordered) as well. Both walk identically here: clears
      // ordered + attribution, PRESERVES open + flag.
      st.ordered = false;
      st.refImageUrl = ""; st.supplierRef = ""; st.eta = ""; st.adminNote = "";
      st.orderedBy = ""; st.orderedTime = ""; st.orderedFullTs = ""; st.orderedDateStr = "";
      // 2026-09-20: if that `ordered` row had re-opened a CLOSED lifecycle (or was
      // a brand-new order), un-ordering returns the item to its pre-order state:
      // closed. The sticky flag (untouched above) keeps it on the Reorders page.
      if (st.reopenedByOrder) {
        st.open = false;
        st.reopenedByOrder = false;
      }
    } else if (ev.action === "ordered") {
      // 2026-09-20: remember when THIS order is what re-opens a CLOSED lifecycle
      // (Collect already tapped, or never requested) so the Inbound receipt can
      // close it again. An order on a still-open request leaves the marker alone.
      if (!st.open) st.reopenedByOrder = true;
      st.open = true;
      st.ordered = true;
      st.refImageUrl = ev.photoPath ? urlFor(ev.photoPath) : "";
      st.supplierRef = ev.supplierRef || "";
      st.eta = ev.eta || "";
      st.adminNote = ev.note || "";
      st.orderedBy = nm(ev.staffId);
      st.orderedTime = hhmm(ev.ts);
      st.orderedFullTs = ev.ts || "";
      st.orderedDateStr = ev.dateStr || "";
      // Brand-new order with no prior request: it IS the request provenance.
      if (!st.requestedBy) {
        st.requestedBy = nm(ev.staffId);
        st.requestedTime = hhmm(ev.ts);
        st.requestedFullTs = ev.ts || "";
        st.requestedDateStr = ev.dateStr || "";
        st.type = ev.type || "standard";
      }
    } else if (ev.action === "delivered") {
      st.deliveredAt = hhmm(ev.ts);          // sheet stores the London HH:mm
      st.deliveredBy = nm(ev.staffId);
      st.lastStatus = "collected";           // the writer always stamps 'collected'
      if (ev.photoPath) {
        // Photo-confirmed: the box is physically here — full close.
        st.open = false;
        st.flag = null;
        st.ordered = false;
        st.resolvedDate = ev.dateStr || "";
      } else {
        // Immediate-close model (every site since 2026-05-06): close the
        // request but KEEP a sticky flag for the Collect-tab filters.
        st.open = false;
        st.resolvedDate = ev.dateStr || "";
      }
      st.reopenedByOrder = false;   // 2026-09-20: closed on its own terms
    } else if (ev.action === "received") {
      st.open = false;
      st.resolvedDate = ev.dateStr || "";
      st.reopenedByOrder = false;
    }
    // custom_inbound / anything else: not part of this feed (already filtered).
  }

  // Emit: still open, OR still flagged (sticky), OR closed TODAY ("Collected
  // Today"). Then the deactivated-item filter: non-custom items missing from
  // the active catalog are hidden (custom items always emit); null set = fail
  // open, exactly like the handler when its catalog read errors.
  const rows = [];
  for (const item of Object.keys(itemState)) {
    const s = itemState[item];
    if (!s.open && !s.flag && s.resolvedDate !== todayStr) continue;
    const isCustom = s.type === "custom";
    if (!isCustom && activeItemNames && !activeItemNames.has(String(s.item || "").toLowerCase())) continue;
    rows.push({
      id: String(s.srcId),          // sheet: last-touched row index; here: last-touched row uuid (stable, unique — screens key on it)
      item: s.item,
      time: s.requestedTime,
      requestedFullTs: s.requestedFullTs,
      dateStr: s.requestedDateStr || todayStr,
      staffName: s.requestedBy,
      requestedBy: s.requestedBy,
      lastBumpedBy: s.lastBumpedBy || "",
      lastBumpedTime: s.lastBumpedTime || "",
      lastBumpedFullTs: s.lastBumpedFullTs || "",
      lastBumpedDateStr: s.lastBumpedDateStr || "",
      bumpCount: s.bumpCount || 0,
      flaggedBy: s.flaggedBy || "",
      flaggedFullTs: s.flaggedFullTs || "",
      status: s.open ? (s.ordered ? "ordered" : s.flag ? s.flag : (s.deliveredAt ? "delivered" : "requested")) : "delivered",
      flag: s.flag,
      closed: !s.open,
      custom: s.type === "custom",
      deliveredAt: s.deliveredAt,
      deliveredBy: s.deliveredBy,
      ordered: !!s.ordered,
      refImageUrl: s.refImageUrl || "",
      supplierRef: s.supplierRef || "",
      eta: s.eta || "",
      adminNote: s.adminNote || "",
      orderedBy: s.orderedBy || "",
      orderedTime: s.orderedTime || "",
      orderedFullTs: s.orderedFullTs || "",
      orderedDateStr: s.orderedDateStr || "",
      resolvedDate: s.resolvedDate || "",
    });
  }
  return rows;
}

// Convenience: both steps in one call (what the fetch layer + tests use).
export function reconstructStock(requests, activity, opts = {}) {
  return walkStock(buildTimeline(requests, activity), opts);
}

// =============================================================================
// CC "Open Reorders" OVERLAY (interim until JJ's CC dual-writes `ordered` rows
// into stock_activity). The Command Centre marks reorders straight onto the
// sheet (note "Marked via magic link" / "Marked from Open Reorders", staff
// "Reorder Owner" or the recipient's name) — invisible to Supabase. A flipped
// kitchen without this overlay would go SILENTLY blind to live admin reorders
// (ruled out 16 Jul). Given the still-authoritative sheet feed for the same
// site, copy the ordered state onto our reconstructed rows.
// Pure + unit-testable; the fetch layer passes the sheet rows in.
// =============================================================================
export function overlayCcOrdered(rows, sheetRows) {
  if (!Array.isArray(rows) || !Array.isArray(sheetRows) || !sheetRows.length) return rows;
  const bySheetItem = new Map();
  for (const sr of sheetRows) {
    if (sr && sr.ordered === true && sr.item) bySheetItem.set(String(sr.item).toLowerCase(), sr);
  }
  if (!bySheetItem.size) return rows;
  const seen = new Set();
  const out = rows.map((r) => {
    const sr = bySheetItem.get(String(r.item || "").toLowerCase());
    if (!sr) return r;
    seen.add(String(r.item).toLowerCase());
    // Our row is CLOSED but the sheet still shows the item ON ORDER: the sheet
    // holds an event we can't see (e.g. a CC 'ordered' placed AFTER the
    // delivery — its walker reopens the item). During dual-run the sheet is
    // the event SUPERSET, so its whole row wins the conflict.
    if (r.closed) return { ...sr, _ccOverlay: true };
    // Open our side: copy the ordered mark + admin metadata onto our row.
    // (status: the handler ranks ordered above flag for open items.)
    return {
      ...r,
      ordered: true,
      status: "ordered",
      orderedBy: sr.orderedBy || r.orderedBy,
      orderedTime: sr.orderedTime || r.orderedTime,
      orderedFullTs: sr.orderedFullTs || r.orderedFullTs,
      orderedDateStr: sr.orderedDateStr || r.orderedDateStr,
      supplierRef: sr.supplierRef || r.supplierRef,
      eta: sr.eta || r.eta,
      adminNote: sr.adminNote || r.adminNote,
      refImageUrl: sr.refImageUrl || r.refImageUrl,
      _ccOverlay: true,
    };
  });
  // Sheet-only ordered items (e.g. a CC fresh inbound that never had a kiosk
  // request) must still appear — the sheet row is already feed-shaped.
  for (const [key, sr] of bySheetItem) {
    if (!seen.has(key)) out.push({ ...sr, _ccOverlay: true });
  }
  return out;
}

// =============================================================================
// Reads (network). Supabase-first with a SHEET FALLBACK on any failure — the
// running list is operationally load-bearing (the kitchen packs the van off
// it), and the read policy is `to authenticated`, so a signed-out session
// returns ZERO rows — indistinguishable from "nothing needed". Same posture as
// sellouts.js: ensure the anon session first; fall back to the sheet on ANY
// error; an EMPTY list WITH a healthy session is a valid state.
// =============================================================================

// Page through a query — Supabase caps a single response (default 1000 rows).
// The walk needs the FULL history (the sheet walks its whole log); a silent
// truncation would quietly corrupt derived state months from now.
async function fetchAll(buildQuery) {
  const page = 1000;
  let from = 0, all = [];
  for (;;) {
    const { data, error } = await buildQuery().range(from, from + page - 1);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < page) return all;
    from += page;
  }
}

async function staffNamesFor(ids) {
  const map = new Map();
  const unique = [...new Set(ids.filter(Boolean))];
  await Promise.all(unique.map(async (id) => {
    try { const { data } = await supabase.rpc("staff_name", { p_id: id }); if (data) map.set(id, data); }
    catch (e) { /* name stays 'Unknown' */ }
  }));
  return map;
}

// Public URL for a stock-photos object (photo_path column). Best-effort — a
// broken URL only affects the reference-image thumbnail, never row state.
// Backfilled rows (source='sheet_backfill') carry the sheet's ABSOLUTE photo
// URL in photo_path — pass those through untouched.
function stockPhotoUrl(path) {
  if (/^https?:\/\//i.test(String(path || ""))) return String(path);
  try { return supabase.storage.from("stock-photos").getPublicUrl(path)?.data?.publicUrl || ""; }
  catch (e) { return ""; }
}

// =============================================================================
// M1 COMPAT SHIM (Lane M / S26) — serve the FOH-till read from the SQL twin
// `stock_till_requests_feed` (migration 0220) instead of fetching two tables and
// replaying walkStock() in the browser. The twin returns the walkStock() shape
// FIELD-FOR-FIELD with two deliberate deltas the shim closes:
//   * rowIndex -> stable `id`: the twin already keys each row on the last-touched
//     uuid, so screens key on `id` (nothing to do here beyond consuming it).
//   * photo_path -> SIGNED URL: the twin never builds a storage URL (no secret in
//     SQL) — it hands back the raw object key in `refImagePath` and leaves
//     `refImageUrl:''`. This shim signs it client-side.
//
// INERT by construction: reached only when stockReadEnabled() is true, and
// STOCK_READ_HOSTS is EMPTY (a flip is a reviewed one-line host add — NOT here).
// The twin is granted to service_role only today, so the anon session cannot call
// it until the daytime flip adds an Edge Function / grant wrapper; until then this
// path fails closed and fetchStockRequests falls back to the live read.
// =============================================================================

// BATCH-sign the private stock-photos object keys in ONE storage call, then
// return a resolver. Only ordered items carry a refImagePath, so this is usually
// a handful of keys or none — but signing them per row is a round-trip each, so
// we sign the whole set at once (createSignedUrls, plural) to keep the till
// render snappy. Absolute (sheet_backfill) URLs pass through untouched.
// Best-effort — a broken URL only dims a thumbnail, never row state.
async function signStockPhotos(paths) {
  const map = new Map();
  const keys = [...new Set((paths || []).filter((p) => p && !/^https?:\/\//i.test(String(p))))];
  if (keys.length) {
    try {
      const { data } = await supabase.storage.from("stock-photos").createSignedUrls(keys, 3600);
      for (const d of data || []) if (d && d.path && d.signedUrl) map.set(d.path, d.signedUrl);
    } catch (e) { /* best-effort: leave unsigned, a broken thumbnail never breaks a row */ }
  }
  return (p) => { const s = String(p || ""); if (!s) return ""; if (/^https?:\/\//i.test(s)) return s; return map.get(s) || ""; };
}

// --- non-blocking CC-ordered overlay ----------------------------------------
// The CC-ordered overlay needs the OLD Apps Script getStockRequests feed (the CC
// marks reorders straight onto the sheet — see overlayCcOrdered). That feed is
// SLOW (measured 5-12s, the kitchen feed up to 20s on 31 Aug 2026), so awaiting it
// on every read defeats the whole point of flipping the read onto Supabase — the
// running list would still block on Apps Script. But the overlay is LOAD-BEARING:
// an on-order item wrongly read as ordered:false drops under the red out-of-stock
// banner AND becomes deliverable with NO photo (the 22 May regression). So we must
// never just skip it, and we must not let a wrong snapshot flip it to false.
//
// The fix: cache the last successful feed per site (module-level, so it survives
// across screen opens for the life of the SPA — kiosks stay open all day), and
// refresh it SINGLE-FLIGHT (one fetch per site at a time). Single-flight is not
// optional: the feed is slow and reads poll every 5-60s across 4 sites, so without
// it refreshes pile up AND land OUT OF ORDER, letting an older snapshot clobber a
// newer one and flap a genuinely-ordered item back to ordered:false. One in flight
// per site makes completions strictly newest-wins and stops hammering the endpoint.
//   * WARM (cache present): apply the cached ordered marks INSTANTLY, refresh in the
//     background. A failed/malformed refresh keeps the last-good marks (a feed
//     outage never blanks ordered state — strictly safer than the old always-await,
//     which overlaid nothing on any error).
//   * COLD (no cache yet): await one fetch, then cache. If it fails, serve the
//     Supabase rows unmarked for this read only; the next read retries.
//
// ⚠️ STALENESS — READ BEFORE ADDING A HOST TO STOCK_READ_HOSTS. The overlay is up
// to ONE display-poll stale, which is up to ~60s on the kitchen: its poll relaxes
// to 60s while the Supabase doorbell is live, and CC 'ordered' marks are sheet-only
// so they do NOT ring that doorbell. So for up to ~60s after an admin marks an item
// ordered, a flipped kitchen can still read it ordered:false (red-banner + no-photo
// window). Today's unflipped kitchen reads the sheet directly and has no such lag.
// This window is inherent to not awaiting the slow feed; closing it needs a
// dedicated sub-poll overlay refresh, or (the real fix) the CC dual-writing
// 'ordered' into stock_activity so the reader sees it with no sheet at all. NEEDS
// JJ/Adam sign-off before the flip. Interim, like the overlay itself.
const _ccOverlayCache = new Map();      // siteKey -> { rows: sheetRequests[], ts }
const _ccOverlayInflight = new Map();   // siteKey -> Promise (single-flight guard)

// Fetch one site's getStockRequests feed, single-flight per site, caching it on
// success. Resolves to the feed's requests[] or null on ANY failure — never throws
// and never poisons the cache with a malformed/partial payload (mirrors the empty
// `requests: []` a quiet site legitimately returns, but rejects a non-array).
function fetchOverlayFeed(siteKey, sheetUrl) {
  const inflight = _ccOverlayInflight.get(siteKey);
  if (inflight) return inflight;
  const p = (async () => {
    try {
      const r = await fetch(sheetUrl);
      if (!r.ok) { console.warn("[stock-read] CC overlay feed " + r.status + " — keeping last-good marks"); return null; }
      const j = await r.json();
      if (!j || !Array.isArray(j.requests)) return null;   // malformed: stay on last-good, don't poison
      _ccOverlayCache.set(siteKey, { rows: j.requests, ts: Date.now() });
      return j.requests;
    } catch (e) {
      console.warn("[stock-read] CC overlay feed unavailable — keeping last-good marks:", e?.message || e);
      return null;
    } finally {
      _ccOverlayInflight.delete(siteKey);
    }
  })();
  _ccOverlayInflight.set(siteKey, p);
  return p;
}

// Apply the CC-ordered overlay to `rows` without blocking on the slow feed when we
// already have a copy. Returns the overlaid rows (or the input rows if the overlay
// is genuinely unavailable). Never throws into the caller.
const CC_OVERLAY_COLD_WAIT_MS = 1500;

export async function applyCcOverlay(rows, siteKey, sheetUrl) {
  if (!sheetUrl) return rows;
  const cached = _ccOverlayCache.get(siteKey);
  if (cached) {
    fetchOverlayFeed(siteKey, sheetUrl).catch(() => {});   // warm: refresh behind, never awaited
    try { return overlayCcOrdered(rows, cached.rows); } catch (e) { return rows; }
  }
  // Cold: give the Apps Script feed a short window, then paint without the marks.
  // A cold /exec answers in 5–25 s (measured on the Trafford launch test, 20 Sep
  // 2026) and the tablet's list was waiting on it; the fetch keeps running behind
  // and fills the cache, so the marks arrive on the next read (the tile polls
  // every 60 s) instead of holding the whole list hostage.
  const fresh = await Promise.race([
    fetchOverlayFeed(siteKey, sheetUrl),
    new Promise((resolve) => setTimeout(() => resolve(undefined), CC_OVERLAY_COLD_WAIT_MS)),
  ]);
  if (fresh) { try { return overlayCcOrdered(rows, fresh); } catch (e) { return rows; } }
  return rows;   // cold + feed slow/unavailable: serve Supabase rows, marks arrive next read
}

// Fetch ONE site's running list from the twin, resolving refImagePath -> signed
// refImageUrl. Returns { requests, _source:'supabase-twin' } or null on any
// failure (caller falls back). ccOverlay keeps the interim CC-ordered overlay.
export async function fetchStockRequestsViaTwin(siteKey, { sheetUrl, ccOverlay = true } = {}) {
  try {
    await ensureAnonSession();
    const { data, error } = await supabase.rpc("stock_till_requests_feed", { p_site: siteKey });
    if (error || !data || !Array.isArray(data.requests)) return null;
    const resolvePhoto = await signStockPhotos(data.requests.map((r) => r.refImagePath));   // one batch call
    let rows = data.requests.map((r) => ({ ...r, refImageUrl: resolvePhoto(r.refImagePath) }));
    if (ccOverlay && sheetUrl) rows = await applyCcOverlay(rows, siteKey, sheetUrl);
    return { requests: rows, _source: "supabase-twin" };
  } catch (e) { return null; }
}

// The sheet's getStockRequests for ONE site, rebuilt from Supabase.
//   sheetUrl:        the existing feed URL (fallback + CC overlay source).
//   activeItemNames: Set of lowercase active catalog names (null = no filter).
//   ccOverlay:       true (default) = overlay CC-ordered marks from the sheet
//                    feed; overlay failure degrades gracefully (warn, no rows
//                    lost) rather than failing the read.
// Returns { requests: [...], _source: "supabase" | "sheet" } or null.
export async function fetchStockRequests(siteKey, { sheetUrl, activeItemNames = null, ccOverlay = true } = {}) {
  const fallback = async (why) => {
    console.warn("[stock-read] running list fell back to the sheet:", why);
    if (!sheetUrl) return null; // screen keeps last-good state
    try { const r = await fetch(sheetUrl); if (!r.ok) return null; const j = await r.json(); return { ...(j || {}), _source: "sheet" }; } catch (e) { return null; }
  };
  try {
    // M1 dark-launch: when the read is flipped (stockReadEnabled — EMPTY gate, no
    // host added here), serve from the SQL twin; fall through to the live
    // two-table read on any failure. Inert until Adam's daytime per-site flip.
    if (stockReadEnabled()) {
      const viaTwin = await fetchStockRequestsViaTwin(siteKey, { sheetUrl, ccOverlay });
      if (viaTwin) return viaTwin;
    }
    await ensureAnonSession();
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return fallback("no site id for " + siteKey);
    const [requests, activity] = await Promise.all([
      fetchAll(() => supabase.from("stock_requests")
        .select("id, item_name, type, status, request_date, requested_by, requested_at, received_by, received_at, updated_at")
        .eq("site_id", siteId).order("requested_at", { ascending: true })),
      fetchAll(() => supabase.from("stock_activity")
        .select("id, item_name, event, flag, ordered, supplier_ref, eta, note, photo_path, staff_id, idem_key, created_at")
        .eq("site_id", siteId).order("created_at", { ascending: true })),
    ]);
    const ids = [
      ...requests.map((r) => r.requested_by), ...requests.map((r) => r.received_by),
      ...activity.map((a) => a.staff_id),
    ];
    const names = await staffNamesFor(ids);
    let rows = reconstructStock(requests, activity, {
      nameFor: (id) => names.get(id) || null,
      urlFor: stockPhotoUrl,
      activeItemNames,
    });
    // Interim CC-ordered overlay (see applyCcOverlay): non-blocking when warm, so a
    // flipped read no longer waits on the slow Apps Script feed. Degrades gracefully
    // (serves Supabase rows without marks) rather than failing the read.
    if (ccOverlay && sheetUrl) rows = await applyCcOverlay(rows, siteKey, sheetUrl);
    return { requests: rows, _source: "supabase" };
  } catch (e) {
    return fallback("threw: " + (e?.message || e));
  }
}
